import { wccModules } from 'enums/wccModules';
import { DOMHelpers } from 'helpers/dom';
import { inject, injectable } from 'inversify';
import { Observable, Subscribable, SubscribableOrNullableValue } from 'knockout';
import { NodeTracker as Tracker } from 'managers/nodes/tracker';
import { withEffect } from 'mixins/withEffect';
import { EffectsContainer } from 'mixins/withEffects';
import WCCAttachment from 'models/attachments/attachment';
import WCCImage from 'models/attachments/image';
import WCCVideo from 'models/attachments/video';
import WCCUpload from 'models/upload';
import { AttachmentCancelControlVMConfig } from './cancel';
import AttachmentControlVM from './control';
import './controls.html';
import './controls.scss';
import { AttachmentCustomControlVMConfig } from './custom';
import { AttachmentDownloadControlVMConfig } from './download';
import { AttachmentImageExpandControlVMConfig } from './imageExpand';
import { AttachmentProgressControlVMConfig } from './progress';
import { AttachmentSelectControlVMConfig } from './select';
import { AttachmentVideoExpandControlVMConfig } from './videoExpand';

components
    .registerLocal('attachment-controls-progress', () => require('./progress'))
    .registerLocal('attachment-controls-select', () => require('./select'))
    .registerLocal('attachment-controls-cancel', () => require('./cancel'))
    .registerLocal('attachment-controls-image-expand', () => require('./imageExpand'))
    .registerLocal('attachment-controls-video-expand', () => require('./videoExpand'))
    .registerLocal('attachment-controls-custom', () => require('./custom'));

const maxControlSize = 40;
const maxRingSize = 60;
const minControlSize = 26;

type AttachmentControlsMode = 'hover' | 'static'
type AttachmentControlsPosition = 'center' | 'top-right'
type AttachmentControlsType = 'round' | 'square'

export interface WCCAttachmentControlsVMConfig {
    progress?: AttachmentProgressControlConfig
    expand?: SubscribableOrNullableValue<AttachmentImageExpandControlVMConfig | WCCAttachment>
    select?: Observable<boolean>
    cancel?: () => void,
    download?: SubscribableOrNullableValue<WCCAttachment>
    custom?: Array<AttachmentCustomControlVMConfig>
    mode?: AttachmentControlsMode,
    position?: AttachmentControlsPosition
    controlsType?: AttachmentControlsType
}

interface AttachmentProgressControlConfig {
    value: SubscribableOrNullableValue<number>
    icon?: SubscribableOrNullableValue<string>
}

@injectable()
export default class WCCAttachmentControlsVM {
    private width = ko.observable(0);
    private height = ko.observable(0);

    private isActive = ko.observable(false);

    private visibleControls: Subscribable<Array<AttachmentControlVM>>
    private isSingleControlMode: Subscribable<boolean>

    mainControls = ko.observableArray<AttachmentControlVM>();

    styles: Subscribable<string>

    bodyNodes: Array<Node>
    customControlsNodes: Array<Node>   

    cancelConfig?: AttachmentCancelControlVMConfig

    isProgressLayerShown: Subscribable<boolean>
    isMainLayerShown: Subscribable<boolean>

    progressConfig = ko.pureComputed(() => this.getProgressConfig());
    imageExpandConfig = ko.pureComputed(() => this.getImageExpandConfig());
    videoExpandConfig = ko.pureComputed(() => this.getVideoExpandConfig());
    selectConfig = ko.pureComputed(() => this.getSelectConfig());
    downloadConfig = ko.pureComputed(() => this.getDownloadConfig());
    customControlsConfigs = ko.pureComputed(() => this.getCustomControlsConfigs())

    constructor(
        @inject(wccModules.componentConfig) private config: WCCAttachmentControlsVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        const mode = ko.pureComputed(() => this.getMode(config.mode));
        const controlsType = ko.pureComputed(() => this.getControlsType(config.controlsType));

        this.cancelConfig = this.getCancelConfig(config.cancel);

        const id = _.uniqueId('wcc-attachment-controls-');

        this.bodyNodes = this.getBodyNodes(info.templateNodes);
        this.customControlsNodes = this.getCustomControlsNodes(info.templateNodes);

        const hasProgress = this.progressConfig.isNotNull();
        const isSelected = this.selectConfig.pluck('isSelected', false);

        this.isMainLayerShown = ko.pureComputed(() => isSelected() || this.isActive());
        this.isProgressLayerShown = ko.pureComputed(() => hasProgress() && !this.isMainLayerShown());

        const isShown = ko.pureComputed(() => this.isProgressLayerShown() || this.isMainLayerShown());
        const isReady = this.mainControls.all(control => control.isReady());

        const controlScale = ko.pureComputed(() => controlsType() === 'square' ? 0.85 : 1);
        const iconScale = ko.pureComputed(() => controlsType() === 'square' ? 1.25 : 1);

        const controlSize = ko.pureComputed(() => this.getControlSize(0.2, 0.25, maxControlSize) * controlScale());
        const iconSize = ko.pureComputed(() => system.toEven(Math.floor(controlSize() * 0.55)) * iconScale());
        const offset = ko.pureComputed(() => system.toEven(Math.floor(controlSize() * 0.33)));

        this.styles = ko.pureComputed(() => `
    #${id} {
        font-size: ${iconSize()}px;
    }

    #${id} .wcc-attachment-controls__control {
        width: ${controlSize()}px;
        height: ${controlSize()}px;
    }

    #${id} .wcc-attachment-controls__layer {
        gap: ${offset()}px;
    }`);

        const { width, height } = new Tracker({ node: info.element as HTMLElement });

        const modeClass = ko.pureComputed(() => this.getModeClass(mode()));
        const positionClass = ko.pureComputed(() => this.getPositionClass(config.position));
        const controlsTypeClass = ko.pureComputed(() => this.getControlsTypeClass(controlsType()));
        const allClasses = ko.pureComputed(() => `${modeClass()} ${positionClass()} ${controlsTypeClass()}`);

        this.visibleControls = this.mainControls.filter(control => control.isVisible());
        this.isSingleControlMode = this.visibleControls.is(controls => controls.length == 1);

        effects.register(isShown => {
            if (isShown) {
                return withEffect((width, height) => {
                    this.width(width);
                    this.height(height);
                }, [width, height]);
            }
        }, [isShown]);

        effects.register(mode => {
            switch (mode) {
                case 'static':
                    this.isActive(true);
                    break;
                case 'hover':
                    this.isActive(false);

                    return [
                        DOMHelpers.onNodeEvent(info.element as HTMLElement, 'mouseenter', () => this.onMouseEnter()),
                        DOMHelpers.onNodeEvent(info.element as HTMLElement, 'mouseleave', () => this.onMouseLeave())
                    ]
            }
        }, [mode]);

        ko.applyBindingsToNode(info.element, {
            'click': (_: any, e: JQueryEventObject) => this.onClick(e),

            'visible': isReady,

            'attr': {
                'id': id
            },

            'css': {
                'wcc-attachment-controls': true,
                'wcc-attachment-controls--single-control': this.isSingleControlMode
            }
        }, this);

        ko.applyBindingsToNode(info.element, { 'css': allClasses }, this);
    }

    private getProgressConfig(): AttachmentProgressControlVMConfig | undefined {
        const config = this.config.progress;

        if (config != undefined)
            return {
                progress: ko.flattenComputed(config.value, 0),
                icon: ko.flattenComputed(config.icon, ''),
                size: ko.pureComputed(() => this.getControlSize(0.33, 0.75, maxRingSize))
            };
    }

    private getImageExpandConfig(): AttachmentImageExpandControlVMConfig | undefined {
        const config = ko.unwrap(this.config.expand);

        if (config instanceof WCCAttachment) {
            if (config instanceof WCCUpload && config.preview() != undefined)
                return { src: config.preview, title: config.name };

            if (config instanceof WCCImage && config.hqUrl() != undefined)
                return { src: config.hqUrl, title: (config as any).title };
        } else if (ko.unwrap(config?.src) != undefined) {
            return config;
        }
    }

    private getVideoExpandConfig(): AttachmentVideoExpandControlVMConfig | undefined {
        const config = ko.unwrap(this.config.expand);

        if (config instanceof WCCVideo && config.lqUrl() != undefined)
            return { video: config }
    }

    private getSelectConfig(): AttachmentSelectControlVMConfig | undefined {
        if (this.progressConfig() == undefined) {
            const isSelected = this.config.select;

            if (ko.isWritableObservable(isSelected))
                return { isSelected }
        }
    }

    private getDownloadConfig(): AttachmentDownloadControlVMConfig | undefined {
        if (this.progressConfig() == undefined)
            return { attachment: this.config.download }
    }

    private getCustomControlsConfigs() {
        return this.config.custom ?? [];
    }

    private getBodyNodes(nodes: Array<Node>) {
        const bodyNodes = $(nodes).filter('controls-body').contents().toArray() as Array<Node>;

        if (bodyNodes.length > 0)
            return bodyNodes;

        return nodes;
    }

    private getCustomControlsNodes(nodes: Array<Node>) {
        const customControlsNodes = $(nodes).filter('controls-custom').contents().toArray() as Array<Node>;

        if (customControlsNodes.length > 0)
            return customControlsNodes;

        return [];
    }

    private getCancelConfig(handler?: Action) {
        if (handler != undefined)
            return <AttachmentCancelControlVMConfig>{
                click: () => handler()
            }
    }

    private getControlSize(maxRelWidth: number, maxRelHeight: number, maxSize: number) {
        const width = this.width();
        const height = this.height();

        const maxWidth = _([Math.floor(width * maxRelWidth), maxSize]).min();
        const maxHeight = _([Math.floor(height * maxRelHeight), maxSize]).min();

        let size = maxWidth < maxHeight ? maxWidth : maxHeight;

        if (size > maxHeight)
            size = maxHeight;

        if (size < minControlSize)
            size = minControlSize;

        return system.toEven(size);
    }

    private getMode(mode?: AttachmentControlsMode): AttachmentControlsMode {
        switch (mode) {
            case 'static': return 'static';
            default: return 'hover';
        }
    }

    private getControlsType(type?: AttachmentControlsType): AttachmentControlsType {
        switch (type) {
            case 'square': return 'square';
            default: return 'round';
        }
    }

    private getModeClass(mode: AttachmentControlsMode) {
        switch (mode) {
            case 'static': return 'wcc-attachment-controls--static';
            default: return 'wcc-attachment-controls--hover';
        }
    }

    private getPositionClass(position?: AttachmentControlsPosition): string {
        switch (position) {
            case 'top-right': return 'wcc-attachment-controls--top-right';
            default: return 'wcc-attachment-controls--center';
        }
    }

    private getControlsTypeClass(type: AttachmentControlsType): string {
        switch (type) {
            case 'square': return 'wcc-attachment-controls--square';
            default: return 'wcc-attachment-controls--round';
        }
    }

    private onMouseEnter() {
        this.isActive(true);
    }

    private onMouseLeave() {
        this.isActive(false);
    }

    private onClick(e: JQueryEventObject) {
        if (this.isSingleControlMode() && $(e.target).closest('.wcc-attachment-controls__control').length == 0) {
            const control = this.visibleControls()[0];

            if (control != undefined)
                control.click();
        }
    }
}